"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");

exports.__esModule = true;
exports.default = useForm;
exports.getField = getField;
exports.updateFields = updateFields;

var _react = require("react");

var _recursiveUnbracket = _interopRequireDefault(require("../../libraries/utils/recursiveUnbracket"));

/* istanbul ignore file */
// deps
// libraries

/**
 * Met à jour en profondeur un élément.
 * @param {object} param0
 * @param {object} param0.fields
 * @param {"input" | "checkbox" | "switch"} param0.mode
 * @param {object} param0.target
 * @param {object} options
 * @param {string} options.duplicateBehavior
 * @param {any} options.index
 * @param {Function} [options.postProcess]
 * @return {object}
 */
function updateFields({
  fields,
  mode = "input",
  target
}, options = {}) {
  const {
    duplicateBehavior = "ignore",
    index: preferedIndex,
    postProcess
  } = options;
  const {
    name,
    value,
    checked
  } = target;
  const namePath = (0, _recursiveUnbracket.default)(name);
  let newFields = { ...fields
  };
  let backtracking = newFields;
  let parentBacktracking = backtracking;
  namePath.forEach(function (name, index) {
    if (
    /* S’il s’agit d’une clé profonde. */
    index < namePath.length - 1) {
      /* Mute tous les objets/tableaux ou les crée à la volée si nécessaire */
      if (Array.isArray(backtracking[name])) {
        backtracking = [...backtracking[name]];
      } else if ("object" === typeof backtracking[name]) {
        backtracking = { ...backtracking[name]
        };
      } else {
        if ("string" === typeof name) {
          backtracking = {};
        } else if ("number" === typeof name) {
          backtracking = [];
        }
      }

      parentBacktracking[name] = backtracking;
      parentBacktracking = backtracking;
    } else {
      switch (mode) {
        case "input":
          {
            if (undefined === value) {
              delete backtracking[name];
            } else {
              backtracking[name] = value;
            }

            break;
          }

        case "switch":
          {
            if (checked) {
              backtracking[name] = value;
            } else {
              delete backtracking[name];
            }

            break;
          }

        case "checkbox":
          {
            if (!backtracking[name]) {
              backtracking[name] = [];
            }

            if (checked) {
              if ("ignore" !== duplicateBehavior || !backtracking[name].includes(value)) {
                if (undefined !== preferedIndex) {
                  backtracking[name][preferedIndex] = value;
                } else {
                  backtracking[name].push(value);
                }
              }
            } else {
              backtracking[name].splice(backtracking[name].indexOf(value), 1);
            }

            break;
          }
      }
    }

    return newFields;
  });

  if (postProcess) {
    newFields = postProcess({
      fields,
      newFields,
      target
    });
  }

  return newFields;
}
/**
 * Retourne la valeur d’un champ en profondeur.
 * @param {object} param0
 * @param {object} param0.fields
 * @param {string} param0.name
 * @return {any}
 */


function getField({
  fields,
  name
}) {
  let backtracking = fields;
  const namePath = (0, _recursiveUnbracket.default)(name);
  namePath.forEach(function (name) {
    var _backtracking;

    backtracking = (_backtracking = backtracking) == null ? void 0 : _backtracking[name];
  });
  return backtracking;
}
/**
 * @param {object} [options]
 * @param {object} [options.initialFields]
 * @param {object} [options.defaultFields]
 * @param {object} [options.initialErrors]
 * @param {function} options.onSubmit
 * @param {boolean} [options.initialDirty=false]
 * @param {boolean} [options.shouldScrollToOnError=false]
 * @param {any} [options.postProcessUpdate]
 */


function useForm(options = {}) {
  const {
    initialFields = {},
    defaultFields = options.initialFields,
    initialErrors = {},
    onSubmit,
    initialDirty = false,
    shouldScrollToOnError = false,
    postProcessUpdate
  } = options;
  const savedDefaultFields = (0, _react.useRef)(defaultFields);
  const [dirty, setDirty] = (0, _react.useState)(initialDirty);
  const [fields, setFields] = (0, _react.useState)(initialFields);
  const [errors, setErrors] = (0, _react.useState)(initialErrors);
  const [requireSubmit, setRequireSubmit] = (0, _react.useState)({});
  const checkRequireSubmit = (0, _react.useCallback)(function (options) {
    const {
      submit = false
    } = options;

    if (submit) {
      setRequireSubmit({});
    }
  }, []);
  const setValue = (0, _react.useCallback)(function (name, value, options = {}) {
    const {
      dirty = true
    } = options;
    checkRequireSubmit(options);
    setFields(function (previousFields) {
      return updateFields({
        fields: previousFields,
        target: {
          name,
          value
        }
      });
    });

    if (dirty) {
      setDirty(true);
    }
  }, [checkRequireSubmit]);
  const setValues = (0, _react.useCallback)(function (values, options = {}) {
    const {
      dirty = true
    } = options;
    checkRequireSubmit(options);
    setFields(values);

    if (dirty) {
      setDirty(true);
    }
  }, [checkRequireSubmit]);
  const setBulkValue = (0, _react.useCallback)(function (targets, options = {}) {
    const {
      dirty = true
    } = options;
    checkRequireSubmit(options);
    setFields(function (previousFields) {
      let fields = previousFields;
      targets.forEach(function (target) {
        var _target$mode;

        fields = updateFields({
          fields,
          target,
          mode: (_target$mode = target == null ? void 0 : target.mode) != null ? _target$mode : "input"
        });
      });
      return fields;
    });

    if (dirty) {
      setDirty(true);
    }
  }, [checkRequireSubmit]);
  const resetValue = (0, _react.useCallback)(function (name, options = {}) {
    const {
      dirty = true
    } = options;
    checkRequireSubmit(options);
    setFields(function (previousFields) {
      return updateFields({
        fields: previousFields,
        target: {
          name,
          value: getField({
            fields: savedDefaultFields,
            name
          })
        }
      });
    });

    if (dirty) {
      setDirty(false);
    }
  }, [savedDefaultFields, checkRequireSubmit]);
  const clearValue = (0, _react.useCallback)(function (name, options = {}) {
    checkRequireSubmit(options);
    setFields(function (previousFields) {
      return updateFields({
        fields: previousFields,
        target: {
          name,
          value: ""
        }
      });
    });
  }, [checkRequireSubmit]);
  const getValue = (0, _react.useCallback)(function (name, fallback, options = {}) {
    checkRequireSubmit(options);
    const field = getField({
      fields,
      name
    });
    return field != null ? field : fallback;
  }, [fields, checkRequireSubmit]);
  const clearValues = (0, _react.useCallback)(function (options = {}) {
    checkRequireSubmit(options);
    setFields({});
  }, [setFields, checkRequireSubmit]);
  const setError = (0, _react.useCallback)(function (name, error) {
    setErrors(function (previousErrors) {
      return updateFields({
        fields: previousErrors,
        target: {
          name,
          value: error
        },
        mode: "input"
      });
    });
  }, []);
  const setBulkError = (0, _react.useCallback)(function (targets) {
    setErrors(function (previousErrors) {
      let fields = previousErrors;
      targets.forEach(function (target) {
        fields = updateFields({
          fields,
          target: { ...target,
            value: target == null ? void 0 : target.error
          },
          mode: "input"
        });
      });
      return fields;
    });
  }, [setErrors]);
  const resetErrors = (0, _react.useCallback)(function () {
    setErrors({});
  }, []);
  const getError = (0, _react.useCallback)(function (name) {
    if (!name) {
      return errors;
    }

    const error = getField({
      fields: errors,
      name
    });
    return error != null ? error : null;
  }, [errors]);
  const resetValues = (0, _react.useCallback)(function (options = {}) {
    checkRequireSubmit(options);
    setFields(savedDefaultFields.current);
  }, [setFields, checkRequireSubmit]);
  const handleChangeInput = (0, _react.useCallback)(function (event) {
    setFields(function (previousFields) {
      return updateFields({
        fields: previousFields,
        target: event.target,
        mode: "input"
      }, {
        postProcess: postProcessUpdate
      });
    });
    setDirty(true);
  }, [postProcessUpdate]);
  const handleChangeSwitch = (0, _react.useCallback)(function (event) {
    setFields(function (previousFields) {
      return updateFields({
        fields: previousFields,
        target: event.target,
        mode: "switch"
      }, {
        postProcess: postProcessUpdate
      });
    });
    setDirty(true);
  }, [postProcessUpdate]);
  const previousRequireSubmit = (0, _react.useRef)(requireSubmit);
  /**
   * Lance l’appel de la soumission des données.
   */

  (0, _react.useEffect)(function () {
    if (requireSubmit !== previousRequireSubmit.current) {
      previousRequireSubmit.current = requireSubmit;
      onSubmit(fields);
    }
  }, [fields, onSubmit, requireSubmit]);
  const $formRef = (0, _react.useRef)();
  (0, _react.useEffect)(function () {
    if (Object.keys(errors).length > 0) {
      if (shouldScrollToOnError) {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "smooth"
        });
      }

      if ($formRef.current instanceof HTMLElement) {
        requestAnimationFrame(function () {
          $formRef.current.scrollIntoView({
            behavior: "smooth"
          });
        });
      }
    }
  }, [errors, shouldScrollToOnError]);
  return {
    formRef: $formRef,
    fields,
    errors,
    dirty,
    setValue,
    setValues,
    setBulkValue,
    resetValue,
    clearValue,
    resetValues,
    clearValues,
    getValue,
    setErrors,
    setError,
    setBulkError,
    resetErrors,
    getError,

    /**
     * @param {object} [options={}]
     * @param {string} options.name
     * @param {Function} [options.validate]
     * @param {boolean} [options.passBulk=false]
     * @param {string} [options.defaultValue=""]
     */
    inputProps(options) {
      const {
        name,
        validate,
        passBulk = false,
        defaultValue = "",
        attributeNames = {
          name: "name",
          value: "value",
          onChange: "onChange",
          onBlur: "onBlur",
          onChangeBulk: "onChangeBulk"
        }
      } = options != null ? options : {};
      const field = getField({
        fields,
        name
      });
      return {
        [attributeNames.name]: name,
        [attributeNames.value]: field != null ? field : defaultValue,
        ...(passBulk && {
          [attributeNames.onChangeBulk]: setBulkValue
        }),
        [attributeNames.onChange]: handleChangeInput,
        [attributeNames.onBlur]: validate ? function (event) {
          const error = validate(event.target.value);
          setErrors(function (previousErrors) {
            return updateFields({
              fields: previousErrors,
              target: {
                name,
                value: true === error ? null : error
              },
              mode: "input"
            });
          });
        } : undefined
      };
    },

    switchProps(options = {}) {
      const {
        name,
        value,
        attributeNames = {
          name: "name",
          value: "value",
          checked: "isChecked",
          onChange: "onChange"
        }
      } = options;
      const field = getField({
        fields,
        name
      });
      return {
        [attributeNames.name]: name,
        [attributeNames.value]: value,
        [attributeNames.checked]: field === String(value),
        [attributeNames.onChange]: handleChangeSwitch
      };
    },

    checkboxProps(options = {}) {
      const {
        name,
        value,
        index,
        attributeNames = {
          name: "name",
          value: "value",
          checked: "isChecked",
          onChange: "onChange"
        }
      } = options;
      const field = getField({
        fields,
        name
      });
      return {
        [attributeNames.name]: name,
        [attributeNames.value]: value,
        [attributeNames.checked]: (field != null ? field : []).includes(String(value)),

        [attributeNames.onChange](event) {
          setFields(function (previousFields) {
            return updateFields({
              fields: previousFields,
              target: event.target,
              mode: "checkbox"
            }, {
              index,
              postProcessUpdate
            });
          });
          setDirty(true);
        }

      };
    },

    onSubmit(event) {
      if (event && event.target.elements) {
        var _event$preventDefault;

        (_event$preventDefault = event.preventDefault) == null ? void 0 : _event$preventDefault.call(event);
        const invalidFieldElements = Array.from(event.target.elements).filter(function (element) {
          return element.name && !element.dataset.ignoreValidation && !element.checkValidity();
        });
        const errors = invalidFieldElements.map(function (invalidFieldElement) {
          return {
            name: invalidFieldElement.name,
            error: [invalidFieldElement.validationMessage]
          };
        });

        if (errors.length > 0) {
          setBulkError(errors);
          return;
        }
      }

      onSubmit(fields);
    }

  };
}